// Meadow sim ported from widgets/rabbits_and_foxes_meadow_population_cycles.html.
// Agent rules and parameters are kept as tuned there; the only change is a seeded rng
// and an optional grass layer (tier 2) that rabbits must eat to breed.

import type { Rng } from '@kit/rng';

export interface Rabbit { x: number; y: number; a: number; e: number }
export interface Fox { x: number; y: number; a: number; e: number; cd: number }

export const P = { rs: 0.004, fs: 0.0045, b: 0.006, sight: 0.05, ct: 0.018, gain: 150, cd: 120, e0: 250, repro: 500, cost: 250 };
export const WH = 0.72;          // world height in width units
export const K = 140;            // rabbit carrying capacity
export const FMAX = 40;
export const BUSHES: [number, number][] = [[0.2, 0.2], [0.78, 0.3], [0.45, 0.58]];
export const BR = 0.08;
export const GRASS_N = 10;       // grass grid is GRASS_N x round(GRASS_N*WH)

export interface Meadow {
  R: Rabbit[]; F: Fox[];
  hist: [number, number, number][];   // rabbits, foxes, grass% (0-100)
  tick: number;
  rescue: boolean;
  grass: boolean;                      // tier 2: grass layer on
  g: number[];                         // grass amounts 0..1 per cell
  regrow: number;                      // grass regrowth per tick (slider)
  events: string[];                    // messages for the UI, drained each frame
}

export function createMeadow(rng: Rng, grass = false): Meadow {
  const m: Meadow = { R: [], F: [], hist: [], tick: 0, rescue: true, grass, g: [], regrow: 0.004, events: [] };
  reset(m, rng);
  return m;
}

export function rab(x: number, y: number, rng: Rng): Rabbit { return { x, y, a: rng() * 6.28, e: 200 }; }
export function fox(x: number, y: number, rng: Rng): Fox { return { x, y, a: rng() * 6.28, e: P.e0, cd: 0 }; }

export function reset(m: Meadow, rng: Rng) {
  m.R = []; m.F = []; m.hist = []; m.tick = 0; m.rescue = true;
  for (let i = 0; i < 50; i++) m.R.push(rab(rng(), rng() * WH, rng));
  for (let i = 0; i < 3; i++) m.F.push(fox(rng(), rng() * WH, rng));
  const rows = Math.round(GRASS_N * WH);
  m.g = Array.from({ length: GRASS_N * rows }, () => 1);
}

export function hidden(r: { x: number; y: number }): boolean {
  for (const [bx, by] of BUSHES) { const dx = r.x - bx, dy = r.y - by; if (dx * dx + dy * dy < BR * BR) return true; }
  return false;
}

function wall(o: { x: number; y: number; a: number }) {
  if (o.x < 0 || o.x > 1) { o.a = Math.PI - o.a; o.x = Math.max(0, Math.min(1, o.x)); }
  if (o.y < 0 || o.y > WH) { o.a = -o.a; o.y = Math.max(0, Math.min(WH, o.y)); }
}

export function cellOf(x: number, y: number): number {
  const rows = Math.round(GRASS_N * WH);
  const cx = Math.min(GRASS_N - 1, Math.max(0, Math.floor(x * GRASS_N)));
  const cy = Math.min(rows - 1, Math.max(0, Math.floor((y / WH) * rows)));
  return cy * GRASS_N + cx;
}

export function grassPct(m: Meadow): number {
  if (!m.g.length) return 100;
  return Math.round((m.g.reduce((a, b) => a + b, 0) / m.g.length) * 100);
}

export function step(m: Meadow, rng: Rng) {
  const nb: Rabbit[] = [], n = m.R.length;
  for (let i = 0; i < n; i++) {
    const r = m.R[i];
    r.a += (rng() - 0.5) * 0.6; r.x += Math.cos(r.a) * P.rs; r.y += Math.sin(r.a) * P.rs; wall(r);
    if (m.grass) {
      // eat where you stand; breeding needs a full belly; starve without grass
      const ci = cellOf(r.x, r.y);
      if (m.g[ci] > 0.05) { const bite = Math.min(m.g[ci], 0.02); m.g[ci] -= bite; r.e = Math.min(400, r.e + bite * 400); }
      r.e -= 0.6;
      if (r.e > 150 && rng() < P.b * (1 - n / K)) nb.push(rab(r.x, r.y, rng));
    } else if (rng() < P.b * (1 - n / K)) nb.push(rab(r.x, r.y, rng));
  }
  if (m.grass) m.R = m.R.filter(r => r.e > 0);
  for (const r of nb) m.R.push(r);
  const nf: Fox[] = [];
  for (const f of m.F) {
    if (f.cd > 0) f.cd--;
    let best = -1, bd = P.sight * P.sight;
    if (f.cd <= 0) for (let i = 0; i < m.R.length; i++) { if (hidden(m.R[i])) continue; const dx = m.R[i].x - f.x, dy = m.R[i].y - f.y, d = dx * dx + dy * dy; if (d < bd) { bd = d; best = i; } }
    if (best >= 0) { const r = m.R[best]; f.a = Math.atan2(r.y - f.y, r.x - f.x); if (bd < P.ct * P.ct) { m.R.splice(best, 1); f.e += P.gain; f.cd = P.cd; } }
    else f.a += (rng() - 0.5) * 0.5;
    const sp = f.cd > 0 ? P.fs * 0.4 : P.fs; f.x += Math.cos(f.a) * sp; f.y += Math.sin(f.a) * sp; wall(f); f.e -= 1;
    if (f.e > P.repro && m.F.length + nf.length < FMAX) { f.e -= P.cost; nf.push(fox(f.x, f.y, rng)); }
  }
  m.F = m.F.filter(f => f.e > 0); for (const f of nf) m.F.push(f);
  if (m.grass) for (let i = 0; i < m.g.length; i++) m.g[i] = Math.min(1, m.g[i] + m.regrow);
  if (m.R.length < 2 && rng() < 0.02) { m.R.push(rab(0, rng() * WH, rng)); m.events.push('A rabbit hopped in from the next meadow.'); }
  if (m.rescue && m.F.length < 1 && rng() < 0.004) { m.F.push(fox(0, rng() * WH, rng)); m.events.push('A fox wandered in from the woods.'); }
  m.tick++;
  if (m.tick % 40 === 0) { m.hist.push([m.R.length, m.F.length, grassPct(m)]); if (m.hist.length > 420) m.hist.shift(); }
}

/** count rabbit cycles in the history: a fall from >= hi to <= lo and back */
export function rabbitCycles(hist: [number, number, number][], hi = 60, lo = 30): number {
  let cycles = 0, state: 'up' | 'down' = 'up';
  for (const [r] of hist) {
    if (state === 'up' && r <= lo) state = 'down';
    else if (state === 'down' && r >= hi) { state = 'up'; cycles++; }
  }
  return cycles;
}
